import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { getPermissionContract } from '../../components/onboarding/providerPermissions';
import type { ConnectorScope, OrbitAsk, OrbitRoomFact, OrbitToolBoard, OrbitToolRow } from '../../types/aai';
import { ErrorNote, Loading } from '../../components/aa';
import { ToolOpportunities } from './ToolOpportunities';

/*
 * Phase 3: this company's tools, one at a time.
 *
 * Not a catalog. The list is what the company itself named when it answered
 * "which tools does your team use", in that order, with the departments that
 * say they work in each kind of tool. A company that has named none has nothing
 * to connect yet, and is sent to answer that card rather than shown two hundred
 * providers to shop through.
 *
 * One decision per tool, and it is the one people actually have: is this
 * connection the whole team's, or only yours. Everything else on the card is
 * the connection's own contract, stated rather than switched, because what a
 * tool lets Orbit read is settled by the grant on the tool's own site and a
 * switch that cannot change it would be a lie told while someone is deciding
 * how far to trust us.
 */

/* This tool's dive questions still waiting on somebody, counted from the
   frame's own asks rather than stored, so the strip cannot claim progress the
   maps do not have. */
const openDives = (row: OrbitToolRow, asks: OrbitAsk[]): number =>
  asks.filter((a) => a.scope === `tool:${row.key}` && a.field_key.startsWith('tc.')).length;

/* What the dive has to show for itself: answers in, questions still out. */
const diveLine = (row: OrbitToolRow, open: number): string => {
  const answered = row.dive_answered || 0;
  if (open === 0 && answered === 0) return 'Deep dive open, no questions yet';
  if (open === 0) return `Deep dive done, ${answered} answered`;
  return `Deep dive: ${answered} answered, ${open} still open`;
};

const initials = (text: string): string => {
  const words = text.replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

type Reach = 'team' | 'me';

const reachOf = (row: OrbitToolRow): Reach => {
  if (row.connector && row.connector.organization_status === 'connected') return 'team';
  return 'me';
};

const isConnected = (row: OrbitToolRow): boolean => {
  if (!row.connector) return false;
  return row.connector.personal_status === 'connected' || row.connector.organization_status === 'connected';
};

/* Can somebody in this department reach this tool?
 *
 * The Owner's decision when there is one, and the category guess when there is
 * not. An unassigned tool reads as reachable: the company runs on it and nobody
 * has narrowed it, so hiding it would be this screen inventing a restriction.
 * The same rule the server applies, kept here only for the filter's counts. */
const reaches = (tool: OrbitToolRow, slug: string): boolean => {
  if (tool.assignment === 'all') return true;
  if (tool.assignment === 'selected') return tool.assigned_slugs.includes(slug);
  // No decision: available if the company actually runs on it, and not if it is
  // only a catalogue row nobody has said anything about. The same rule the
  // server applies when it narrows the board for a department's own reader.
  return tool.on_stack;
};

/* The second lens: not WHO a tool is for but where it stands. */
type ToolState = 'all' | 'connected' | 'unconnected';

const STATE_FILTERS: { id: ToolState; label: string }[] = [
  { id: 'all', label: 'Any state' },
  { id: 'connected', label: 'Connected' },
  { id: 'unconnected', label: 'Not connected' },
];

const inState = (tool: OrbitToolRow, state: ToolState): boolean => {
  if (state === 'all') return true;
  if (state === 'connected') return isConnected(tool);
  return !isConnected(tool);
};

export const PhaseTools: React.FC<{
  clientId: string;
  facts: OrbitRoomFact[];
  asks: OrbitAsk[];
  onBack: () => void;
  onChanged: () => void;
  /** Sends the reader to the card where tools are named. */
  onNameTools: () => void;
  /** Opens the frame, where every card including a dive's own is answered. */
  onOpenFrame: () => void;
}> = ({ clientId, facts, asks, onBack, onChanged, onNameTools, onOpenFrame }) => {
  const [board, setBoard] = useState<OrbitToolBoard | null>(null);
  // The Overview filter: the whole company, or one department's tools. Reads
  // the DECISION, and falls back to the category guess for a tool nobody has
  // decided on yet, because hiding an unassigned tool from a department would
  // invent a restriction the Owner never set.
  const [lens, setLens] = useState('all');
  const [state, setState] = useState<ToolState>('all');
  const [query, setQuery] = useState('');
  // Tools Composio can reach that this board does not carry yet.
  const [found, setFound] = useState<{ slug: string; label: string; logo: string; source: string }[]>([]);
  const [addingSlug, setAddingSlug] = useState('');
  /* Parked with the dive: every listed tool already gets the whole tool intake.
  const [diving, setDiving] = useState('');

  /* Going deeper on a tool is a decision about THIS tool, so it is taken here,
     standing in front of it. It is not done for every tool a company names:
     seven questions against a dozen tools is a survey, and the board exists so
     somebody can pick the two or three that carry the work. * /
  const dive = async (row: OrbitToolRow) => {
    setDiving(row.key);
    try {
      const res = await aaiApi.openToolDive({ tool_key: row.key, tool_label: row.label });
      await load();
      onChanged();
      if (res.data.created > 0) {
        toast.success(`${res.data.created} question${res.data.created === 1 ? '' : 's'} about ${row.label}, asked in the frame.`);
      } else {
        toast.info(`${row.label} has no questions left to ask.`);
      }
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Could not open the dive.');
    } finally {
      setDiving('');
    }
  };
  */

  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState('');
  const [busy, setBusy] = useState('');
  // What the person has chosen for a tool they have not connected yet.
  const [reach, setReach] = useState<Record<string, Reach>>({});
  const pollRef = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const res = await aaiApi.getOrbitTools();
      setBoard(res.data);
      setOpen((current) => {
        if (current) return current;
        const next = res.data.tools.find((t) => !isConnected(t)) || res.data.tools[0];
        return next ? next.key : '';
      });
    } catch {
      setError('Could not load this company\'s tools.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => window.clearInterval(pollRef.current), []);

  /* A beat behind the typing. Only ever offers; nothing is added unchosen. */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setFound([]);
      return undefined;
    }
    let live = true;
    const timer = window.setTimeout(async () => {
      try {
        const res = await aaiApi.searchTools(q);
        if (live) setFound(res.data.tools || []);
      } catch {
        if (live) setFound([]);
      }
    }, 350);
    return () => { live = false; window.clearTimeout(timer); };
  }, [query]);

  const addTool = async (tool: { slug: string; label: string; logo: string }) => {
    setAddingSlug(tool.slug);
    try {
      await aaiApi.addCompanyTool({ slug: tool.slug, label: tool.label, logo: tool.logo });
      toast.success(`${tool.label} is on your tools now. Connect it below.`);
      setQuery('');
      setFound([]);
      await load();
      onChanged();
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || `Could not add ${tool.label}.`);
    } finally {
      setAddingSlug('');
    }
  };

  const reachFor = (row: OrbitToolRow): Reach => reach[row.key] || reachOf(row);

  const confirmConnected = async (slug: string, scope: ConnectorScope): Promise<boolean> => {
    for (let i = 0; i < 8; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await aaiApi.getConnectorStatus(slug, scope);
        if (res.data.connected) return true;
      } catch {
        // The popup may still be finishing; keep asking.
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { window.setTimeout(resolve, 1300); });
    }
    return false;
  };

  const connect = async (row: OrbitToolRow) => {
    if (!row.connector) return;
    const slug = row.connector.slug;
    const scope: ConnectorScope = reachFor(row) === 'team' ? 'organization' : 'personal';
    setBusy(row.key);
    try {
      const res = await aaiApi.initiateConnector(slug, scope);
      if (res.data.alreadyConnected) {
        toast.info(`${row.label} was already authorized, so there was nothing to approve.`);
        await load();
        onChanged();
        setBusy('');
        return;
      }
      if (!res.data.redirectUrl) {
        toast.error(`Could not start the ${row.label} connection.`);
        setBusy('');
        return;
      }
      const popup = window.open(res.data.redirectUrl, `connect-${slug}`, 'width=600,height=720,menubar=no,toolbar=no');
      if (!popup || popup.closed) {
        window.location.href = res.data.redirectUrl;
        return;
      }
      const started = Date.now();
      pollRef.current = window.setInterval(async () => {
        if (popup.closed) {
          window.clearInterval(pollRef.current);
          const ok = await confirmConnected(slug, scope);
          await load();
          setBusy('');
          if (ok) {
            const word = scope === 'organization' ? 'The whole team can use it.' : 'It stays yours alone.';
            toast.success(`${row.label} connected. ${word} Read-only until an action is turned on.`);
            onChanged();
            return;
          }
          toast.error(`${row.label} did not finish connecting. If you closed the window early, try again.`);
          return;
        }
        if (Date.now() - started > 10 * 60 * 1000) {
          window.clearInterval(pollRef.current);
          setBusy('');
        }
      }, 900);
    } catch {
      toast.error(`Could not start the ${row.label} connection.`);
      setBusy('');
    }
  };

  /* Changing the reach of a connection that already exists is a real move, not
     a preference: sharing needs its own sign-in, and narrowing takes the shared
     one away from colleagues. */
  const setReachFor = async (row: OrbitToolRow, next: Reach) => {
    if (!row.connector) return;
    setReach((r) => ({ ...r, [row.key]: next }));
    if (!isConnected(row)) return;
    const slug = row.connector.slug;
    if (next === 'team' && row.connector.organization_status !== 'connected') {
      toast.info(`Sign in once more to share ${row.label} with the team.`);
      await connect({ ...row });
      return;
    }
    if (next === 'me' && row.connector.organization_status === 'connected') {
      const ok = window.confirm(
        `Keep ${row.label} to yourself?\n\nColleagues using the shared connection lose it until someone shares one again.`,
      );
      if (!ok) {
        setReach((r) => ({ ...r, [row.key]: 'team' }));
        return;
      }
      setBusy(row.key);
      try {
        await aaiApi.revokeConnector(slug, 'organization');
        await load();
        toast.success(`${row.label} is yours alone now.`);
      } catch {
        toast.error(`Could not change who can use ${row.label}.`);
      } finally {
        setBusy('');
      }
    }
  };

  const next = (row: OrbitToolRow) => {
    if (!board) return;
    const i = board.tools.findIndex((t) => t.key === row.key);
    const following = board.tools[i + 1];
    if (!following) {
      onBack();
      return;
    }
    setOpen(following.key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) return <ErrorNote message={error} />;
  if (!board) return <Loading label="Loading this company's tools" />;

  const connected = board.tools.filter(isConnected).length;
  // Opportunities are only offered on a connected tool: on one that is not,
  // they are a second decision stacked on the first.
  const waiting = board.tools.filter(isConnected).reduce((sum, t) => sum + (t.opportunities || 0), 0);
  // One list to search, because there is one list: everything a connection can
  // be held to, with the company's own answers marked on it.
  const needle = query.trim().toLowerCase();
  const hit = (label: string) => !needle || label.toLowerCase().includes(needle);
  const shown = board.tools
    .filter((t) => lens === 'all' || reaches(t, lens))
    .filter((t) => inState(t, state))
    .filter((t) => hit(t.label));
  // Only what the board does not carry, and only to whoever can add it:
  // otherwise the screen offers a dead end with a button on it.
  const onBoard = new Set(board.tools.map((t) => t.key));
  const discoverable = found.filter((t) => !onBoard.has(t.slug.toLowerCase()));
  const showDiscoverable = Boolean(needle) && discoverable.length > 0;


  const head = (
    <div className="det-head">
      <button type="button" className="back" onClick={onBack}>← Company</button>
      <h1>Tools &amp; access</h1>
      <span className="pct">
        <b>{connected}</b> of {board.tools.length} connected
        {waiting > 0 && <> · <b>{waiting}</b> opportunities waiting</>}
      </span>
    </div>
  );

  if (board.tools.length === 0) {
    return (
      <>
        {head}
        <div className="empty">
          <strong>This company has not named its tools yet.</strong>
          Phase 3 works on the tools the company actually runs on, so there is nothing to connect until somebody answers which those
          are. It is one card, and the departments&apos; own answers fill in from there.
          <button type="button" onClick={onNameTools}>Name the tools</button>
        </div>
        {facts.length > 0 && (
          <>
            <div className="doors-label" style={{ marginTop: 22 }}>What Orbit read about the stack</div>
            <div className="held-grid open">
              {facts.map((f) => (
                <div key={f.field_key} className="hc">
                  <b>{f.label}</b>
                  <span>{f.value_display || 'nothing recorded'}</span>
                  <small>{f.source}</small>
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {head}
      <div className="dept-intro">
        Every tool a connection can be held to. The ones <b>this company named</b> are marked, and come first. Every tool is open to
        everyone. For each one, decide whether the connection is the whole team&apos;s or yours alone, then connect it.{' '}
        <b>Read-only by default; every action an agent takes on your data is a deliberate, reversible choice.</b> The sign-in happens
        on the tool&apos;s own site, never here.
      </div>

      {/* Named by the company and not in the catalogue. Said rather than listed
          as rows: nothing can be connected, assigned or built on them, and a
          row you cannot act on makes the ones beside it look unreliable too. */}
      {board.unreachable.length > 0 && (
        <div className="rep-note" style={{ marginBottom: 14 }}>
          You also told us about {board.unreachable.join(', ')}. Orbit cannot hold a connection to{' '}
          {board.unreachable.length === 1 ? 'it' : 'them'}, so {board.unreachable.length === 1 ? 'it stays' : 'they stay'} as context
          Orbit reasons about rather than as something to connect or assign.
        </div>
      )}

      <div className="tool-search">
        <Search size={15} className="shrink-0 text-ink-soft" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your tools, or the catalogue for one to add"
          aria-label="Search tools"
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} aria-label="Clear the search">Clear</button>
        )}
      </div>

      {/* Overview: the whole company, or one department. The counts are of the
          tools that department can actually reach, which is the question the
          Owner is asking when they pick one. */}
      {board.departments.length > 0 && (
        <div className="tool-lens">
          <button type="button" className={clsx('lens', lens === 'all' && 'on')} onClick={() => setLens('all')}>
            All company <b>{board.tools.length}</b>
          </button>
          {board.departments.map((d) => (
            <button
              key={d.slug}
              type="button"
              className={clsx('lens', lens === d.slug && 'on')}
              onClick={() => setLens(d.slug)}
            >
              {d.name} <b>{board.tools.filter((t) => reaches(t, d.slug)).length}</b>
            </button>
          ))}
        </div>
      )}

      {/* Across the departments rather than inside one. */}
      <div className="tool-lens sub">
        {STATE_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={clsx('lens', state === f.id && 'on')}
            onClick={() => setState(f.id)}
          >
            {f.label}
            <b>{board.tools.filter((t) => (lens === 'all' || reaches(t, lens)) && inState(t, f.id)).length}</b>
          </button>
        ))}
      </div>

      {/* Under the company's own rows and visibly not one of them: what you
          could have, against what you do. */}
      {showDiscoverable && (
        <>
          <div className="doors-label" style={{ marginTop: 4 }}>
            Also in the Composio catalogue &mdash; not on your tools yet
          </div>
          <div className="tool-found">
            {discoverable.slice(0, 6).map((t) => (
              <div key={t.slug} className="found">
                <span className="lg">{initials(t.label)}</span>
                <span>
                  <strong>{t.label}</strong>
                  <small>{t.source === 'catalog' ? 'We ship a connector for this one' : 'From Composio'}</small>
                </span>
                <button type="button" disabled={addingSlug === t.slug} onClick={() => { void addTool(t); }}>
                  {addingSlug === t.slug ? 'Adding' : 'Add to your tools'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {shown.length === 0 && !needle && (
        <div className="empty">
          <strong>Nothing here under this filter.</strong>
          Widen the department or the state above, or open a tool under All company and say where it belongs.
        </div>
      )}
      {shown.length === 0 && needle && !showDiscoverable && (
        <div className="empty">
          <strong>Nothing matches &ldquo;{query}&rdquo;.</strong>
          Not on your tools, and nothing by that name in the Composio catalogue either.
        </div>
      )}

      {shown.map((row, i) => {
        const contract = getPermissionContract(row.connector ? row.connector.slug : row.key);
        const live = isConnected(row);
        const isOpen = open === row.key;
        const chosen = reachFor(row);
        // Every tool is open to the whole company. Who works in it is a guess
        // from what people do, and says so.
        let usedBy = 'Open to everyone';
        if (row.used_by.length > 0) usedBy = `Probably ${row.used_by.join(' · ')}, from what they work in`;
        const sharedWith = 'everyone in the company';
        return (
          <div key={row.key} className={clsx('tool-card', live ? 'done' : 'now', isOpen && 'open')}>
            <div
              className="tool-head"
              role="button"
              tabIndex={0}
              onClick={() => setOpen(isOpen ? '' : row.key)}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpen(isOpen ? '' : row.key); }}
            >
              <span className="lg">{initials(row.label)}</span>
              <div>
                <strong>
                  {row.label}
                  {row.on_stack && <em className="on-stack">{row.added ? 'Added by you' : 'On your stack'}</em>}
                  {live && (row.opportunities || 0) > 0 && (
                    <em className="on-stack opps">
                      {row.opportunities} {row.opportunities === 1 ? 'opportunity' : 'opportunities'}
                    </em>
                  )}
                </strong>
                <small>{usedBy} · {row.named_by || row.category_label}</small>
                {/* What breaks if this goes. Read from the skills that declare
                    they need it, so the cost of switching it off is on the row
                    rather than found out afterwards. */}
                {row.used_in_skills.length > 0 && (
                  <small className="used-in">
                    Used by {row.used_in_skills.map((k) => k.name).join(', ')}
                  </small>
                )}
              </div>
              <span className={clsx('status-chip', live ? 'conn' : 'off')}>
                <i />{live ? 'Connected' : 'Not connected'}
              </span>
              <em>{live ? 'DONE' : String(i + 1).padStart(2, '0')}</em>
            </div>
            <div className="tool-body">
              {!row.connector && (
                <>
                  <p className="lead">
                    The company runs on {row.label}, but Orbit cannot hold a connection to it from here. It stays on the map as context
                    for everything Orbit reasons about; nothing reads it.
                  </p>
                  <div className="tool-cta">
                    <button type="button" className="save" onClick={(e) => { e.stopPropagation(); next(row); }}>Save &amp; next</button>
                  </div>
                </>
              )}

              {row.connector && (
                <>
                  {/* Whose sign-in this is.
                      It said "Whole team / Just me", which read as a second way
                      of saying which departments have the tool. It is not: that
                      is the Assign control above. This is whether ONE sign-in
                      serves everyone the tool was assigned to, or whether each
                      person connects their own. The labels name the sign-in now,
                      and the shared one says who "shared" reaches, which is
                      whatever the assignment above says. */}
                  <div className="reach">
                    <div className="reach-t">
                      <strong>Whose sign-in does this use</strong>
                      <small>
                        {chosen === 'team'
                          ? `One company sign-in, used by ${sharedWith}. Nobody else has to connect their own.`
                          : 'Your own sign-in, used only by you. Colleagues connect their own, and nothing of yours is used on their behalf.'}
                      </small>
                    </div>
                    <div className="reach-switch" role="group" aria-label="Whose sign-in this connection uses">
                      <button
                        type="button"
                        className={clsx(chosen === 'team' && 'on')}
                        disabled={busy === row.key || !board.can_manage}
                        onClick={(e) => { e.stopPropagation(); setReachFor(row, 'team'); }}
                      >
                        One company sign-in
                      </button>
                      <button
                        type="button"
                        className={clsx(chosen === 'me' && 'on')}
                        disabled={busy === row.key}
                        onClick={(e) => { e.stopPropagation(); setReachFor(row, 'me'); }}
                      >
                        My own sign-in
                      </button>
                    </div>
                  </div>
                  {!board.can_manage && chosen === 'me' && (
                    <p className="rep-note">Sharing a connection with the team is the owner&apos;s to do.</p>
                  )}

                  <div className="perm-group">
                    <b>Can view · granted by the connection</b>
                    {contract.reads.map((line) => (
                      <div key={line} className="perm">
                        <span>{line}<small>Read-only</small></span>
                        <span className="perm-state on">on</span>
                      </div>
                    ))}
                  </div>

                  {(contract.writes || []).length > 0 && (
                    <div className="perm-group">
                      <b>Can act · off until an agent is built for it</b>
                      {(contract.writes || []).map((line) => (
                        <div key={line} className="perm">
                          <span className="risk">{line}<small>Acts on your data. Approval required each time.</small></span>
                          <span className="perm-state">off</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="perm-group">
                    <b>Never, whatever the tool allows</b>
                    {contract.neverDoes.map((line) => (
                      <div key={line} className="perm">
                        <span>{line}</span>
                        <span className="perm-state never">never</span>
                      </div>
                    ))}
                  </div>

                  <div className="tool-cta">
                    <button
                      type="button"
                      className="connect"
                      disabled={!row.connector.enabled || !row.connector.connect_here || busy === row.key || !board.can_use}
                      onClick={(e) => { e.stopPropagation(); connect(row); }}
                    >
                      {live ? `Reconnect ${row.label}` : `Connect ${row.label}, sign in on ${row.label}`}
                    </button>
                    <button type="button" className="save" onClick={(e) => { e.stopPropagation(); next(row); }}>Save &amp; next</button>
                  </div>

                  {!row.connector.enabled && <div className="grant-trail">Switched off for this company in Connections & access.</div>}
                  {!row.connector.connect_here && row.connector.setup_hint && <div className="grant-trail">{row.connector.setup_hint}</div>}
                  {live && (
                    <div className="grant-trail">
                      {chosen === 'team' ? 'SHARED WITH THE TEAM' : 'YOURS ALONE'} · VIEW-ONLY · REVOKE ANY TIME IN CONNECTIONS & ACCESS
                    </div>
                  )}
                </>
              )}

              {/* The tool's own dossier: what the people who open it do in it.
                  The board knows which tools exist and the Scout can read a
                  connected one; neither knows what somebody does by hand in
                  here, which is the thing an agent would take over. */}
              <div className="tool-dive">
                {/* Parked: no dive any more; every listed tool gets the whole tool intake.
                {!row.dived && (
                  <>
                    <div>
                      <strong>Go deeper on {row.label}</strong>
                      <small>Seven questions about what people actually do in it: the records that matter, the work done by hand, what gets carried out of it. They make every opportunity below evidenced rather than guessed.</small>
                    </div>
                    <button
                      type="button"
                      disabled={diving === row.key}
                      onClick={(e) => { e.stopPropagation(); dive(row); }}
                    >
                      {diving === row.key ? 'Opening…' : 'Go deeper'}
                    </button>
                  </>
                )}
                */}
                {row.dived && (() => {
                  const left = openDives(row, asks);
                  return (
                    <>
                      <div>
                        <strong>{diveLine(row, left)}</strong>
                        <small>
                          {left > 0 && 'They are asked in the frame, with the rest of the company\u2019s cards. What comes back grounds the opportunities below.'}
                          {left === 0 && 'What came back grounds the opportunities below. Ask Orbit to look again and it will cite it.'}
                        </small>
                      </div>
                      {/* The questions are answered in the frame, not here, so
                          the strip carries the way there rather than leaving
                          somebody to find it. Nothing to answer disables it:
                          a button that opens a screen with no cards on it is
                          a dead end dressed as an action. */}
                      <button
                        type="button"
                        disabled={left === 0}
                        title={left === 0 ? `Every dive question about ${row.label} has been answered.` : `Answer the ${left} still open about ${row.label}`}
                        onClick={(e) => { e.stopPropagation(); onOpenFrame(); }}
                      >
                        {left > 0 ? 'Answer these' : 'All answered'}
                      </button>
                    </>
                  );
                })()}
              </div>

              {/* Only while open: otherwise the phase costs a request per tool. */}
              {isOpen && live && (
                <ToolOpportunities
                  toolKey={row.key}
                  toolLabel={row.label}
                  connected={live}
                  clientId={clientId}
                  onChanged={load}
                />
              )}
            </div>
          </div>
        );
      })}

      {asks.length > 0 && (
        <p className="rep-note">
          {asks.length} {asks.length === 1 ? 'question' : 'questions'} about the stack {asks.length === 1 ? 'is' : 'are'} still open.
          They are asked in the frame, with the rest of the company&apos;s cards.
        </p>
      )}

    </>
  );
};

export default PhaseTools;
